import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  LEAF_Q_QUESTIONS,
  scoreLeafQ,
  type LeafQAnswers,
} from "@/lib/red-s/leaf-q";

/**
 * LEAF-Q monthly screen (BW18 PR-H).
 *
 * Renders the 8-question form, scores client-side, persists to
 * `leaf_q_response`. Shown on /health/red-s. A scheduled push reminder
 * fires on the 1st of each month (PR-I push wiring).
 */
export function LeafQForm() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<LeafQAnswers>({});

  const lastScreen = useQuery({
    queryKey: ["leaf_q_last", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return null;
      const { data } = await supabase
        .from("leaf_q_response")
        .select("date, score, symptoms_count, risk_level")
        .eq("user_id", userId)
        .order("date", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("not signed in");
      const result = scoreLeafQ(answers);
      const { error } = await supabase.from("leaf_q_response").upsert(
        {
          user_id: userId,
          date: new Date().toISOString().slice(0, 10),
          questions: answers,
          score: result.score,
          symptoms_count: result.symptoms_count,
          risk_level: result.risk_level,
        },
        { onConflict: "user_id,date" },
      );
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["leaf_q_last", userId] });
      setAnswers({});
    },
  });

  const allAnswered = LEAF_Q_QUESTIONS.every((q) => answers[q.id] != null);

  return (
    <section
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-surface)",
        padding: "var(--space-6)",
      }}
    >
      <h3 style={{ margin: 0, fontSize: "var(--type-body-l)", fontWeight: 500 }}>
        Monthly RED-S screen
      </h3>
      <p
        style={{
          margin: "var(--space-2) 0 var(--space-5)",
          color: "var(--fg-2)",
          fontSize: "var(--type-body-s)",
          lineHeight: 1.5,
        }}
      >
        8 questions. Adapted from the Melin et al. 2014 LEAF-Q for endurance
        athletes on tirzepatide. Score persists in /health/red-s history.
      </p>

      {lastScreen.data && (
        <p
          style={{
            margin: "0 0 var(--space-4)",
            color: "var(--fg-3)",
            fontSize: "var(--type-meta)",
          }}
        >
          Last screen: {lastScreen.data.date}, score {lastScreen.data.score} (
          {lastScreen.data.risk_level} risk).
        </p>
      )}

      {submit.data ? (
        <div
          style={{
            padding: "var(--space-4)",
            background:
              submit.data.risk_level === "high"
                ? "var(--brick-wash, var(--bg-3))"
                : "var(--bg-3)",
            borderRadius: "var(--radius-card)",
            border: "1px solid var(--hair)",
          }}
        >
          <p style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>
            Score: {submit.data.score} ({submit.data.risk_level} risk)
          </p>
          {submit.data.risk_level === "high" && (
            <p
              style={{
                margin: "var(--space-2) 0 0",
                fontSize: "var(--type-body-s)",
                color: "var(--fg-2)",
                lineHeight: 1.5,
              }}
            >
              Several symptoms suggest RED-S risk. Consider reviewing training
              load and fueling with a sports dietitian. The Recovery hero will
              flag this on /today; a suggestion to schedule a DEXA will fire if
              the high-risk pattern persists 3+ months.
            </p>
          )}
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (allAnswered) submit.mutate();
          }}
          style={{ display: "grid", gap: "var(--space-4)" }}
        >
          {LEAF_Q_QUESTIONS.map((q) => (
            <fieldset
              key={q.id}
              style={{
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                padding: "var(--space-3) var(--space-4)",
              }}
            >
              <legend
                style={{
                  fontSize: "var(--type-body-s)",
                  color: "var(--fg)",
                  padding: "0 var(--space-2)",
                }}
              >
                {q.prompt}
              </legend>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "var(--space-2)",
                  marginTop: "var(--space-2)",
                }}
              >
                {q.options.map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: "var(--type-body-s)",
                      color: "var(--fg-2)",
                    }}
                  >
                    <input
                      type="radio"
                      name={q.id}
                      checked={answers[q.id] === opt.value}
                      onChange={() => setAnswers({ ...answers, [q.id]: opt.value })}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
          <button
            type="submit"
            disabled={!allAnswered || submit.isPending}
            style={{
              padding: "var(--space-3) var(--space-5)",
              background: "var(--accent)",
              color: "#0a0b0d",
              fontWeight: 600,
              border: "none",
              borderRadius: "var(--radius-card)",
              cursor: allAnswered ? "pointer" : "not-allowed",
              opacity: allAnswered ? 1 : 0.6,
            }}
          >
            {submit.isPending ? "Saving…" : "Submit screen"}
          </button>
        </form>
      )}
    </section>
  );
}
